import React, { useState } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import { AggregatedSkuDetail, SkuDetail } from 'helpers/hooks/useProduct';
import styles from './file-uploader.module.scss';
import { ErrorObject } from './index';

interface FileUploaderProps {
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  setNotifcationObj: React.Dispatch<React.SetStateAction<ErrorObject>>;
  selectedFile: File | null;
  setSkuDetails: React.Dispatch<React.SetStateAction<SkuDetail[]>>;
  setMissingSkus: React.Dispatch<React.SetStateAction<AggregatedSkuDetail[]>>;
  isEnhancedCsvUploadEnabled: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string;
  invalidSkuDetails?: string[];
  invalidSkuDetailsHeading?: string;
  allSkuDetails?: SkuDetail[];
}

const CSV_HEADERS = {
  SKU: 'sku',
  QUANTITY: 'quantity',
  UPI: 'upi',
  UPC: 'upc',
  LULULEMON_UPI: 'lululemon upi',
  LULULEMON_UPC: 'lululemon upc',
  PARTNER_ITEM_ID: 'partner item id',
} as const;

const REQUIRED_HEADERS = [CSV_HEADERS.SKU, CSV_HEADERS.QUANTITY];
const OPTIONAL_METADATA_HEADERS = [
  CSV_HEADERS.UPI,
  CSV_HEADERS.UPC,
  CSV_HEADERS.LULULEMON_UPI,
  CSV_HEADERS.LULULEMON_UPC,
  CSV_HEADERS.PARTNER_ITEM_ID,
];
const ALLOWED_HEADERS = [...REQUIRED_HEADERS, ...OPTIONAL_METADATA_HEADERS];

const normalizeHeaders = (headers: string[]): Record<string, number> =>
  headers.reduce<Record<string, number>>((map, header, index) => {
    map[header.trim().toLowerCase()] = index;
    return map;
  }, {});

const getCell = (parts: string[], headerMap: Record<string, number>, header: string): string | undefined => {
  const index = headerMap[header];
  const value = index === undefined ? undefined : parts[index]?.trim();
  return value || undefined;
};

const getCellByAliases = (
  parts: string[],
  headerMap: Record<string, number>,
  aliases: string[],
): string | undefined => {
  for (const alias of aliases) {
    const value = getCell(parts, headerMap, alias);
    if (value) {
      return value;
    }
  }

  return undefined;
};

const validateRequiredHeaders = (headerMap: Record<string, number>): string[] =>
  REQUIRED_HEADERS.filter((header) => headerMap[header] === undefined);

const hasOnlyAllowedHeaders = (headerMap: Record<string, number>): boolean =>
  Object.keys(headerMap).every((header) => ALLOWED_HEADERS.includes(header as (typeof ALLOWED_HEADERS)[number]));

const looksLikeLegacyDataRow = (line: string): boolean => {
  const parts = line
    .trim()
    .split(/[\t,]/)
    .map((part) => part.trim());

  if (parts.length < 2) {
    return false;
  }

  const sku = parts[0];
  const quantity = parts[1];
  return /^\d+$/.test(sku) && /^\d+$/.test(quantity) && Number(quantity) > 0;
};

export const getFileValidationMessages = (formatMessage: ReturnType<typeof useFormat>['formatMessage']) => ({
  EMPTY_FILE_SIZE: formatMessage({ id: 'empty_file_size', defaultMessage: "The file can't be blank." }),
  EMPTY_FILE_CONTENT: formatMessage({
    id: 'empty_file_content',
    defaultMessage: "The file can't be blank.",
  }),
  INVALID_FILE_TYPE: formatMessage({ id: 'invalid_file_type', defaultMessage: 'File type must be .csv.' }),
  FILE_READ_ERROR: formatMessage({
    id: 'file_read_error',
    defaultMessage: 'An error occurred while reading the file.',
  }),
  INVALID_HEADER: formatMessage({
    id: 'sku_uploader_invalid_header',
    defaultMessage: 'CSV header is invalid or missing required columns (SKU, Quantity). Please check your file format.',
  }),
  MISSING_HEADER: (filename: string) =>
    formatMessage({
      id: 'missing_header',
      defaultMessage: `${filename} does not have a header.`,
    }),
  UNSUPPORTED_DATA: (filename: string) =>
    formatMessage({
      id: 'unsupported_data',
      defaultMessage: `${filename} includes unsupported data. Please upload a file with just product SKUs and quantities, no headers or other info.`,
    }),
  UNSUPPORTED_DATA_WITH_HEADERS: (filename: string) =>
    formatMessage({
      id: 'unsupported_data_with_headers',
      defaultMessage: `${filename} includes unsupported data. Each row in the file must have a SKU and Quantity. UPC, UPI and Partner Item ID are optional. No other fields are allowed.`,
    }),
  MISSING_DATA: (filename: string) =>
    formatMessage({
      id: 'missing_data',
      defaultMessage:
        `${filename} file contains rows that are missing data.\n\n` +
        ` •  SKU and Quantity are required fields.\n` +
        ` •  UPC, UPI and Partner Item ID should all be populated when any of these\n` +
        `    fields are provided.\n\n` +
        `Please add all missing data to the file and upload it again.`,
    }),
  INVALID_SKUS: (filename: string) =>
    formatMessage({
      id: 'invalid_skus',
      defaultMessage: `${filename} file contains invalid SKUs. Please remove all invalid SKUs and try again.`,
    }),
  SKU_LIMIT_EXCEEDED: (filename: string, overBy: number) =>
    formatMessage({
      id: 'sku_limit_exceeded',
      defaultMessage: `${filename} exceeds the 200 SKU limit. Please remove ${overBy} SKUs and try again.`,
    }),
});

const transformLegacyLinesToObjects = (lines: string[]): SkuDetail[] => {
  return lines.map((line, index) => {
    const [skuId, quantity] = line.split(',');
    return { skuId, quantity: Number(quantity), row: index + 1 };
  });
};

const transformHeaderLinesToObjects = (lines: string[]): SkuDetail[] => {
  if (lines.length === 0) return [];

  const headerMap = normalizeHeaders(lines[0].split(/[\t,]/));
  return lines.slice(1).map((line, index) => {
    const parts = line.split(/[\t,]/);

    return {
      skuId: getCell(parts, headerMap, CSV_HEADERS.SKU) ?? '',
      quantity: Number(getCell(parts, headerMap, CSV_HEADERS.QUANTITY)) || 0,
      row: index + 2,
      upi: getCellByAliases(parts, headerMap, [CSV_HEADERS.UPI, CSV_HEADERS.LULULEMON_UPI]),
      upc: getCellByAliases(parts, headerMap, [CSV_HEADERS.UPC, CSV_HEADERS.LULULEMON_UPC]),
      partnerItemId: getCell(parts, headerMap, CSV_HEADERS.PARTNER_ITEM_ID),
    };
  });
};

export const validateCsvFile = (
  file: File,
  validationMessages: ReturnType<typeof getFileValidationMessages>,
  isEnhancedCsvUploadEnabled = false,
): Promise<ValidationResult> => {
  return new Promise((resolve) => {
    if (file.size === 0) {
      return resolve({
        isValid: false,
        errorMessage: validationMessages.EMPTY_FILE_SIZE,
      });
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      return resolve({
        isValid: false,
        errorMessage: validationMessages.INVALID_FILE_TYPE,
      });
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result !== 'string') {
        return resolve({
          isValid: false,
          errorMessage: validationMessages.FILE_READ_ERROR,
        });
      }

      const normalized = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      if (normalized.trim().length === 0) {
        return resolve({
          isValid: false,
          errorMessage: validationMessages.EMPTY_FILE_CONTENT,
        });
      }

      const lines = normalized
        .trim()
        .split('\n')
        .filter((line) => line.trim().length > 0);

      if (isEnhancedCsvUploadEnabled) {
        const headerMap = normalizeHeaders(lines[0].split(/[\t,]/));
        const missingHeaders = validateRequiredHeaders(headerMap);

        if (missingHeaders.length > 0 && looksLikeLegacyDataRow(lines[0])) {
          return resolve({
            isValid: false,
            errorMessage: validationMessages.MISSING_HEADER(file.name),
          });
        }

        if (!hasOnlyAllowedHeaders(headerMap)) {
          return resolve({
            isValid: false,
            errorMessage: validationMessages.UNSUPPORTED_DATA_WITH_HEADERS(file.name),
          });
        }

        if (missingHeaders.length > 0) {
          return resolve({
            isValid: false,
            errorMessage: validationMessages.INVALID_HEADER,
          });
        }

        if (lines.length < 2) {
          return resolve({
            isValid: false,
            errorMessage: validationMessages.EMPTY_FILE_CONTENT,
          });
        }

        const dataLines = lines.slice(1);
        if (dataLines.length > 200) {
          const overBy = dataLines.length - 200;
          return resolve({
            isValid: false,
            errorMessage: validationMessages.SKU_LIMIT_EXCEEDED(file.name, overBy),
          });
        }

        const allSkuDetails = transformHeaderLinesToObjects(lines);
        const invalidSkus: string[] = [];
        const missingDataRows: string[] = [];

        for (const row of allSkuDetails) {
          const missingFields: string[] = [];

          if (!row.skuId) {
            missingFields.push('SKU');
          }

          if (!Number.isInteger(row.quantity) || row.quantity <= 0) {
            missingFields.push('Quantity');
          }

          const hasAnyMetadata = Boolean(row.upi || row.upc || row.partnerItemId);
          if (hasAnyMetadata) {
            if (!row.upi) {
              missingFields.push('UPI');
            }
            if (!row.upc) {
              missingFields.push('UPC');
            }
            if (!row.partnerItemId) {
              missingFields.push('Partner Item ID');
            }
          }

          if (missingFields.length > 0) {
            missingDataRows.push(`Row ${row.row} - ${missingFields.join(', ')}`);
            continue;
          }

          if (!/^\d+$/.test(row.skuId)) {
            invalidSkus.push(`Row ${row.row}, SKU ${row.skuId || 'MISSING'}`);
          }
        }

        if (missingDataRows.length > 0) {
          return resolve({
            isValid: false,
            errorMessage: validationMessages.MISSING_DATA(file.name),
            invalidSkuDetailsHeading: 'The following rows have missing data:',
            invalidSkuDetails: missingDataRows,
          });
        }

        if (invalidSkus.length > 0) {
          return resolve({
            isValid: false,
            errorMessage: validationMessages.INVALID_SKUS(file.name),
            invalidSkuDetails: invalidSkus,
          });
        }
        return resolve({ isValid: true, errorMessage: '', allSkuDetails });
      }

      if (lines.length > 200) {
        const overBy = lines.length - 200;
        return resolve({
          isValid: false,
          errorMessage: validationMessages.SKU_LIMIT_EXCEEDED(file.name, overBy),
        });
      }

      const invalidSkus: string[] = [];

      for (let i = 0; i < lines.length; i++) {
        const parts = lines[i]
          .trim()
          .split(/[\t,]/)
          .map((p) => p.trim());

        if (parts.length !== 2 || !/^\d+$/.test(parts[1]) || Number(parts[1]) <= 0) {
          return resolve({
            isValid: false,
            errorMessage: validationMessages.UNSUPPORTED_DATA(file.name),
          });
        }

        if (!/^\d+$/.test(parts[0])) {
          invalidSkus.push(`Row ${i + 1}, SKU ${parts[0]}`);
        }
      }

      if (invalidSkus.length > 0) {
        return resolve({
          isValid: false,
          errorMessage: validationMessages.INVALID_SKUS(file.name),
          invalidSkuDetails: invalidSkus,
        });
      }
      const allSkuDetails = transformLegacyLinesToObjects(lines);

      return resolve({ isValid: true, errorMessage: '', allSkuDetails });
    };

    reader.onerror = () =>
      resolve({
        isValid: false,
        errorMessage: validationMessages.FILE_READ_ERROR,
      });

    reader.readAsText(file);
  });
};

const FileUploader: React.FC<FileUploaderProps> = ({
  setSelectedFile,
  setNotifcationObj,
  selectedFile,
  setSkuDetails,
  setMissingSkus,
  isEnhancedCsvUploadEnabled,
}) => {
  const [dragging, setDragging] = useState(false);
  const [inputKey, setInputKey] = useState(Date.now());
  const { formatMessage } = useFormat({ name: 'common_sku_file_uploader' });

  const handleFile = async (file: File | null) => {
    if (!file) return;

    // Handle case for multiple file uploads
    if (selectedFile) {
      setSelectedFile(null);
      setSkuDetails([]);
      setMissingSkus([]);
    }

    const validationMessages = getFileValidationMessages(formatMessage);

    const { isValid, errorMessage, invalidSkuDetails, invalidSkuDetailsHeading, allSkuDetails } = await validateCsvFile(
      file,
      validationMessages,
      isEnhancedCsvUploadEnabled,
    );

    if (isValid) {
      setSelectedFile(file);
      setSkuDetails(allSkuDetails ?? []);

      setNotifcationObj({
        successStatus: true,
        errorStatus: false,
        warningStatus: false,
        responseMessage: '',
        invalidSkuDetailsHeading: '',
        invalidSkuDetails: [],
      });
    } else {
      setSelectedFile(null);
      setSkuDetails([]); // reset sku details if file is invalid
      setNotifcationObj({
        successStatus: false,
        errorStatus: true,
        warningStatus: false,
        responseMessage: errorMessage,
        invalidSkuDetailsHeading: invalidSkuDetailsHeading ?? '',
        invalidSkuDetails: invalidSkuDetails ?? [],
      });
    }

    setInputKey(Date.now());
  };

  const renderFileInput = () => (
    <label className={styles.clickToUpload}>
      {formatMessage({ id: 'click.to.upload', defaultMessage: 'Click to upload' })}
      <input
        key={inputKey}
        type="file"
        onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
        accept=".csv"
        data-testid="partner__uploadFileInput_test-id"
        style={{ display: 'none' }}
      />
    </label>
  );

  return (
    <div
      className={`${styles.dropZone} ${dragging ? styles.dragging : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files?.[0] ?? null);
      }}
      data-testid="partner__FileDropzone_test-id"
    >
      <div className={styles.dropZoneContent}>
        <span>
          {renderFileInput()}{' '}
          {formatMessage({
            id: 'or_drag_and_drop_your_csv_file',
            defaultMessage: 'or drag and drop your CSV File',
          })}
        </span>

        {selectedFile && (
          <div className={styles.uploadNew}>
            <div className={styles.dropZoneCta}>
              <span className={styles.fileName}>{selectedFile.name}</span>
            </div>
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => {
                setSelectedFile(null);
                setInputKey(Date.now());
                setSkuDetails([]); // reset sku details if file is invalid
                setMissingSkus([]); // reset missing skus
                setNotifcationObj({
                  successStatus: false,
                  errorStatus: false,
                  warningStatus: false,
                  responseMessage: '',
                  invalidSkuDetailsHeading: '',
                  invalidSkuDetails: [],
                });
              }}
              aria-label={formatMessage({ id: 'remove.selected.file', defaultMessage: 'Remove selected file' })}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploader;

export const FILE_VALIDATION_MESSAGES = getFileValidationMessages((args) => args.defaultMessage || '');
